import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Link2, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { adminAiot, errText } from "@/lib/aiot";
import { AiTag } from "@/components/AiTag";
import { Attendance } from "@/components/aiot/AttendancePanel";

const Pill = ({ s, testid }) => {
  const c = { pending: "bg-amber-500/15 text-amber-700", approved: "bg-emerald-500/15 text-emerald-700", rejected: "bg-red-500/15 text-red-700" }[s] || "bg-slate-100 text-slate-600";
  return <span className={`px-2 py-0.5 rounded-full text-xs font-bold capitalize ${c}`} data-testid={testid}>{s}</span>;
};
const Th = ({ children }) => <th className="px-3 py-2 font-semibold text-slate-600 whitespace-nowrap text-left">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-3 py-2 whitespace-nowrap ${className}`}>{children}</td>;
const day = (d) => (d ? new Date(d).toLocaleDateString("en-IN") : "");

const Apps = ({ api }) => {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.apps().then(setRows).catch(() => {}), [api]);
  useEffect(() => { load(); }, [load]);
  const decide = (a, action) => api.decide(a.id, action).then(() => { toast.success(`Application ${action === "approve" ? "approved" : "rejected"}`); load(); }).catch((e) => toast.error(errText(e, "Action failed")));
  return (
    <table className="w-full text-sm" data-testid="aiot-admin-apps">
      <thead><tr><Th>Date</Th><Th>Applicant</Th><Th>Contact</Th><Th>Role / education</Th><Th>AI fit</Th><Th>Account</Th><Th>Status</Th><Th>Actions</Th></tr></thead>
      <tbody>
        {rows.map((a) => (
          <tr key={a.id} className="border-t border-slate-200" data-testid={`aiot-app-${a.id}`}>
            <Td>{day(a.created_at)}</Td>
            <Td><div className="font-semibold">{a.name}</div><div className="text-xs text-slate-500">{a.city}</div></Td>
            <Td><div>{a.phone}</div><div className="text-xs text-slate-500">{a.email}</div></Td>
            <Td className="max-w-[240px] whitespace-normal"><div>{a.background}</div>{a.message && <div className="text-xs text-slate-500 line-clamp-2" title={a.message}>{a.message}</div>}</Td>
            <Td><AiTag ai={a.ai} kind="fit" testid={`aiot-app-fit-${a.id}`} /></Td>
            <Td>{a.has_account ? <span className="text-emerald-700 font-bold" data-testid={`aiot-app-account-${a.id}`}>Yes</span> : <span className="text-slate-500" data-testid={`aiot-app-account-${a.id}`}>Not yet</span>}</Td>
            <Td><Pill s={a.status} testid={`aiot-app-status-${a.id}`} /></Td>
            <Td>
              <div className="flex gap-1.5">
                {a.status !== "approved" && <Button size="sm" onClick={() => decide(a, "approve")} className="bg-emerald-600 hover:bg-emerald-700 h-8" data-testid={`aiot-app-approve-${a.id}`}><Check className="w-3.5 h-3.5 mr-1" />Approve</Button>}
                {a.status !== "rejected" && <Button size="sm" variant="outline" onClick={() => decide(a, "reject")} className="h-8" data-testid={`aiot-app-reject-${a.id}`}><X className="w-3.5 h-3.5 mr-1" />Reject</Button>}
              </div>
            </Td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">No applications yet</td></tr>}
      </tbody>
    </table>
  );
};

const Capstone = ({ api, s, onDone }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ score: s.capstone?.score ?? "", feedback: s.capstone?.feedback || "" });
  const save = () => api.capstone(s.user_id, { score: Number(f.score), feedback: f.feedback }).then(() => { toast.success("Capstone graded"); setOpen(false); onDone(); }).catch((e) => toast.error(errText(e, "Could not save")));
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" className="h-8" data-testid={`aiot-capstone-open-${s.user_id}`}><GraduationCap className="w-3.5 h-3.5 mr-1" />Capstone</Button></DialogTrigger>
      <DialogContent data-testid="aiot-capstone-dialog">
        <DialogHeader><DialogTitle>Grade capstone · {s.name}</DialogTitle></DialogHeader>
        <Input type="number" min={0} max={100} placeholder="Score out of 100" value={f.score} onChange={(e) => setF({ ...f, score: e.target.value })} data-testid="aiot-capstone-score" />
        <Textarea rows={3} placeholder="Feedback for the student" value={f.feedback} onChange={(e) => setF({ ...f, feedback: e.target.value })} data-testid="aiot-capstone-feedback" />
        <Button onClick={save} disabled={f.score === "" || f.score < 0 || f.score > 100} data-testid="aiot-capstone-save">Save grade</Button>
      </DialogContent>
    </Dialog>
  );
};

const Students = ({ api }) => {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.students().then(setRows).catch(() => {}), [api]);
  useEffect(() => { load(); }, [load]);
  return (
    <table className="w-full text-sm" data-testid="aiot-admin-students">
      <thead><tr><Th>Student</Th><Th>Email</Th><Th>Status</Th><Th>Started</Th><Th>Week</Th><Th>Tests done</Th><Th>Tests avg</Th><Th>Labs avg</Th><Th>Capstone</Th><Th>Standing</Th><Th>Final</Th><Th>Tab leaves</Th><Th>Grade</Th></tr></thead>
      <tbody>
        {rows.map((s) => (
          <tr key={s.user_id} className="border-t border-slate-200" data-testid={`aiot-student-${s.user_id}`}>
            <Td className="font-semibold">{s.name}</Td><Td>{s.email}</Td><Td><Pill s={s.status === "active" ? "approved" : s.status} /></Td><Td>{s.start_date}</Td><Td>{s.week}</Td>
            <Td>{s.tests_done}/26</Td><Td>{s.tests_avg}%</Td><Td>{s.labs_avg ?? "—"}</Td><Td>{s.capstone?.score ?? "—"}</Td><Td>{s.standing}%</Td>
            <Td>{s.final == null ? "In progress" : <b className={s.passed ? "text-emerald-700" : "text-red-700"}>{s.final}% {s.passed ? "Pass" : "Fail"}</b>}</Td>
            <Td className={s.violations ? "text-amber-700 font-bold" : ""}>{s.violations}</Td>
            <Td><Capstone api={api} s={s} onDone={load} /></Td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={13} className="px-3 py-8 text-center text-slate-500">No approved students yet</td></tr>}
      </tbody>
    </table>
  );
};

const Media = ({ api, c, onDone }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ video_url: c.video_url, live_url: c.live_url, live_at: c.live_at });
  const bad = [f.video_url, f.live_url].some((u) => u && !/^https:\/\/\S+$/.test(u));
  const save = () => api.media(c.id, f).then(({ data }) => {
    toast.success(data.followups ? `Class updated · recording emailed to ${data.followups} student${data.followups > 1 ? "s" : ""} who missed the live class` : "Class updated");
    setOpen(false); onDone();
  }).catch((e) => toast.error(errText(e, "Links must start with https://")));
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" className="h-8" data-testid={`aiot-media-open-${c.id}`}><Link2 className="w-3.5 h-3.5 mr-1" />Attach</Button></DialogTrigger>
      <DialogContent data-testid="aiot-media-dialog">
        <DialogHeader><DialogTitle>Class {c.n}: {c.title}</DialogTitle></DialogHeader>
        <label className="text-sm font-semibold">Recorded video (YouTube, Google Drive or .mp4 link)<Input value={f.video_url} onChange={(e) => setF({ ...f, video_url: e.target.value })} placeholder="https://" data-testid="aiot-media-video" /></label>
        <label className="text-sm font-semibold">Live class link (Meet, Zoom, Jitsi)<Input value={f.live_url} onChange={(e) => setF({ ...f, live_url: e.target.value })} placeholder="https://" data-testid="aiot-media-live" /></label>
        <label className="text-sm font-semibold">Live class date and time<Input type="datetime-local" value={f.live_at} onChange={(e) => setF({ ...f, live_at: e.target.value })} data-testid="aiot-media-live-at" /></label>
        {bad && <p className="text-sm text-red-600" data-testid="aiot-media-error">Links must start with https://</p>}
        <Button onClick={save} disabled={bad} data-testid="aiot-media-save">Save</Button>
      </DialogContent>
    </Dialog>
  );
};

const Classes = ({ api }) => {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.classes().then(setRows).catch(() => {}), [api]);
  useEffect(() => { load(); }, [load]);
  return (
    <table className="w-full text-sm" data-testid="aiot-admin-classes">
      <thead><tr><Th>#</Th><Th>Month</Th><Th>Class</Th><Th>Minutes</Th><Th>Video</Th><Th>Live</Th><Th>Attach</Th></tr></thead>
      <tbody>
        {rows.map((c) => (
          <tr key={c.id} className="border-t border-slate-200" data-testid={`aiot-admin-class-${c.id}`}>
            <Td>{c.n}</Td><Td>M{c.month}</Td><Td className="max-w-[360px] truncate font-semibold">{c.title}</Td><Td>{c.minutes}</Td>
            <Td>{c.video_url ? <span className="text-emerald-700 font-bold">Added</span> : "—"}</Td><Td>{c.live_url ? <span className="text-emerald-700 font-bold">{c.live_at ? new Date(c.live_at).toLocaleString("en-IN") : "Added"}</span> : "—"}</Td>
            <Td><Media key={`${c.video_url}${c.live_url}${c.live_at}`} api={api} c={c} onDone={load} /></Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const AiotAdmin = ({ auth }) => {
  const [sub, setSub] = useState("apps");
  const [api] = useState(() => adminAiot(auth));
  const tabs = [["apps", "Applications"], ["students", "Students & results"], ["classes", "Classes & links"], ["attendance", "Live attendance"]];
  return (
    <div className="mt-4" data-testid="aiot-admin">
      <div className="flex gap-2">
        {tabs.map(([k, l]) => <button key={k} type="button" onClick={() => setSub(k)} className={`px-3.5 py-1.5 rounded-full text-sm font-semibold ${sub === k ? "bg-[#0F766E] text-white" : "bg-white border border-slate-200 text-[#0A192F]"}`} data-testid={`aiot-admin-tab-${k}`}>{l}</button>)}
      </div>
      <div className="mt-4 bg-white border border-slate-200 rounded-xl overflow-x-auto">
        {sub === "apps" && <Apps api={api} />}
        {sub === "students" && <Students api={api} />}
        {sub === "classes" && <Classes api={api} />}
        {sub === "attendance" && <Attendance api={api} />}
      </div>
    </div>
  );
};
